import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Pagination, PencilIcon, Table, Tag, Toggle } from "@mwi/ui";

import { RootState } from "store";
import Breed from "api/types/models/breed";
import { setPagination } from "slices/vocabularyFilters";
import { useGetBreedsQuery, useUpdateBreedMutation } from "api";
import EditBreedVocabularyModal from "components/modals/admin/vocabulary/breed/edit";

const BreedsVocabulary = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams();

  const { filtering, pagination } = useSelector((state: RootState) => ({
    filtering: state.vocabularyFilters.filtering,
    pagination: state.vocabularyFilters.pagination,
  }));

  const { data: breeds, isFetching: fetchingBreeds } = useGetBreedsQuery({
    page: pagination.page,
    size: pagination.size,
    search: filtering.search,
  });

  const [updateBreed] = useUpdateBreedMutation();

  const handleBreedToggle = (breed: Breed, value: boolean) => {
    updateBreed({
      breedUuid: breed.id,
      body: {
        enabled: value,
        translation: breed.translation,
      },
    });
  };

  return (
    <>
      <div className="w-full overflow-x-auto">
        <Table loading={fetchingBreeds}>
          <Table.Head>
            <Table.Row>
              <Table.Item as="th">Breed</Table.Item>
              <Table.Item as="th">Translation</Table.Item>
              <Table.Item as="th">Active</Table.Item>
              <Table.Item as="th">Edit</Table.Item>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {breeds && breeds?.data?.length ? (
              breeds.data.map((breed) => {
                return (
                  <Table.Row key={breed.id}>
                    <Table.Item>{breed.name}</Table.Item>
                    <Table.Item>
                      {breed.translation ? (
                        breed.translation
                      ) : (
                        <Tag variants="outlined">N/A</Tag>
                      )}
                    </Table.Item>
                    <Table.Item>
                      <Toggle
                        value={breed.enabled}
                        onChange={(value) => handleBreedToggle(breed, value)}
                      />
                    </Table.Item>
                    <Table.Item>
                      <Link
                        className="inline-block"
                        to={`/admin/customisations/vocabulary/${breed.id}`}
                      >
                        <PencilIcon className="w-6 h-6 cursor-pointer text-primary" />
                      </Link>
                    </Table.Item>
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Item colSpan={4} className="text-center">
                  No breed found
                </Table.Item>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <div className="mt-4 col-span-full">
        <Pagination
          onPrevPage={() =>
            dispatch(setPagination({ page: pagination.page - 1 }))
          }
          prevPage={pagination.page > 1}
          currentPage={pagination.page}
          nextPage={breeds?.meta?.has_next_page ?? false}
          onNextPage={() =>
            dispatch(setPagination({ page: pagination.page + 1 }))
          }
          loading={fetchingBreeds}
        />
      </div>

      <EditBreedVocabularyModal
        isOpen={!!id && filtering.category === "breeds"}
        onClose={() => navigate("/admin/customisations/vocabulary")}
      />
    </>
  );
};

export default BreedsVocabulary;
